import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '@mukil/i18n';
import { useVisualViewport } from '../../hooks/use_visual_viewport';
import {
  get_bottom_sheet_max_height,
  get_bottom_sheet_keyboard_offset,
  get_panel_animation,
  type DrawerSide,
} from './drawer_layout';

export type { DrawerSide };

export interface DrawerProps {
  /** Whether the drawer is open */
  open: boolean;
  /** Called when the backdrop is clicked or Escape is pressed */
  onClose: () => void;
  /** Header title text */
  title?: string;
  /**
   * Optional descriptive text rendered under the title, matching Dialog's
   * `DialogDescription` styling. Additive -- omitting it leaves every
   * existing consumer's header unchanged.
   */
  description?: string;
  /** Panel body content */
  children: ReactNode;
  /** Optional footer content (rendered inside the footer slot) */
  footer?: ReactNode;
  /** Optional className for the panel element */
  className?: string;
  /**
   * Which edge the panel slides in from. Defaults to `'end'` (the original
   * right-side slide-over) -- every existing consumer is unaffected.
   * `'bottom'` renders a bottom sheet, capped to the visible visual
   * viewport so an on-screen keyboard never covers the footer actions.
   */
  side?: DrawerSide;
  /**
   * Where initial focus lands when the drawer opens. `'body'` (default)
   * focuses the first focusable element inside the body content -- the
   * form field a caller actually wants filled in. `'close'` restores the
   * original behaviour of focusing the header close button first --
   * required for a drawer whose body's first control performs a
   * consequential/mutating action (e.g. an Approve button), where
   * autofocusing it would let a single stray Enter keypress trigger it
   * the instant the drawer opens.
   */
  initialFocus?: 'body' | 'close';
  /**
   * Panel width for `side="end"` (the default right-side slide-over) only --
   * `side="bottom"` already goes full-width and ignores this prop. `'default'`
   * (the default, omitting the prop) keeps the historic fixed `480` width
   * byte-identical for every existing consumer. `'wide'` widens the panel to
   * `760` for content that needs real horizontal room -- e.g. a multi-column
   * table -- without forcing every consumer to pass a one-off `className`
   * width override that fights the panel's own inline `width` style.
   */
  width?: 'default' | 'wide';
  /**
   * Accessible name for the header close button. Defaults to the shared
   * `common` catalog's `close_panel`. Pass it from an app whose own catalog
   * does not carry that namespace, so a screen reader never reads a raw
   * key. Omitting it leaves every existing consumer unchanged.
   */
  closeLabel?: string;
}

const GLASS_STRONG: CSSProperties = {
  background: 'rgba(255,255,255,0.72)',
  backdropFilter: 'blur(28px) saturate(200%)',
  WebkitBackdropFilter: 'blur(28px) saturate(200%)',
  border: '1px solid rgba(255,255,255,0.75)',
  boxShadow:
    '0 1px 0 rgba(255,255,255,0.95) inset, 0 12px 40px -12px rgba(15,23,42,0.14), 0 2px 8px -2px rgba(15,23,42,0.06)',
};

const FOCUSABLE_SELECTORS =
  'a[href]:not([tabindex="-1"]),button:not([disabled]):not([tabindex="-1"]),input:not([disabled]):not([tabindex="-1"]),select:not([disabled]):not([tabindex="-1"]),textarea:not([disabled]):not([tabindex="-1"]),[tabindex]:not([tabindex="-1"])';

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS),
  );
}

/**
 * Drawer (SlideOver) — right-side panel that slides in from the right edge.
 *
 * Renders via React Portal to document.body so it overlays fixed headers.
 * Closes on backdrop click or Escape key.
 * Traps focus while open and restores focus to the trigger on close.
 *
 * @example
 * <Drawer open={open} onClose={() => setOpen(false)} title="Edit invoice">
 *   <p>Content here</p>
 * </Drawer>
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
  side = 'end',
  initialFocus = 'body',
  width = 'default',
  closeLabel,
}: DrawerProps) {
  const { t } = useTranslation('common');
  const generatedId = useId();
  const titleId = title !== undefined ? `${generatedId}-title` : undefined;
  const descriptionId =
    description !== undefined ? `${generatedId}-description` : undefined;
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  /**
   * The element that had focus immediately before this drawer opened, so it
   * can be restored on close. Held in a ref and captured only on the open
   * transition -- never re-captured by a mid-interaction re-render.
   */
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  // Only the bottom-sheet variant needs this data (max-height cap +
  // keyboard offset) -- gate the subscription so a closed drawer, or one
  // using the default side="end", doesn't re-render on every
  // keyboard/pinch-zoom visualViewport event for data it never uses.
  const visualViewport = useVisualViewport(open && side === 'bottom');

  // ── Initial focus + focus restore: once per OPEN, never per render ───────
  //
  // Deliberately does NOT depend on `onClose`. Callers overwhelmingly pass it
  // as an inline arrow (`onClose={() => setOpen(false)}` -- 90+ call sites
  // across the product apps), so its identity changes on every parent render.
  // While this logic shared one effect with the keydown trap below (deps
  // `[open, onClose, initialFocus]`), every re-render tore that effect down
  // and set it back up -- and setup calls `focusable[0].focus()`.
  //
  // The result: typing a single character into ANY field inside a drawer
  // re-rendered the parent, which re-ran this effect, which yanked focus to
  // the drawer's FIRST focusable element -- so the next character landed
  // there. The old cleanup's `previouslyFocused?.focus()` fired on those same
  // spurious re-runs too, adding a second focus jump per keystroke.
  //
  // Reported against WagePro's add-employee form, whose first field is Full
  // name: a letter typed in any field sent the next letter to Full name. It
  // was never that form's bug -- every controlled input in every Drawer had
  // it, and the bottom-sheet variant is hit hardest because
  // `useVisualViewport` re-renders on soft-keyboard resize as well.
  useEffect(() => {
    if (!open) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    const panel = panelRef.current;
    if (panel) {
      // `initialFocus="close"` opts a drawer back into the original
      // behaviour (header close button first) -- for a drawer whose body's
      // first control is a consequential/mutating action (e.g. an Approve
      // button), autofocusing it would let a stray Enter at open trigger
      // it. Default `'body'` prefers the first focusable element inside
      // the body content (the form field a caller actually wants filled
      // in) over the close button, which is first in DOM order but rarely
      // what the user wants focused. Both fall back to the close button
      // (or the panel itself) when the body has nothing focusable -- e.g.
      // a purely informational drawer.
      const bodyFocusable =
        initialFocus === 'body' && bodyRef.current
          ? getFocusableElements(bodyRef.current)
          : [];
      const focusable =
        bodyFocusable.length > 0 ? bodyFocusable : getFocusableElements(panel);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        panel.focus();
      }
    }

    return () => {
      previouslyFocusedRef.current?.focus();
      previouslyFocusedRef.current = null;
    };
  }, [open, initialFocus]);

  // ── Escape-to-close + Tab focus trap ────────────────────────────────────
  //
  // Separate effect because this one genuinely needs the LATEST `onClose`, so
  // it must re-subscribe when that identity changes. Swapping a `keydown`
  // listener is side-effect-free; moving focus is not, which is exactly why
  // the two were split (see the effect above).
  useEffect(() => {
    if (!open) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const panelEl = panelRef.current;
        if (!panelEl) return;
        const focusable = getFocusableElements(panelEl);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const overlayStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 50,
    display: 'flex',
    // `side="bottom"` stacks the flex layout vertically so `justifyContent:
    // 'flex-end'` below pins the panel to the bottom edge instead of the
    // right edge. `side="end"` (default) is untouched -- no `flexDirection`
    // key at all, matching the pre-existing style object exactly.
    ...(side === 'bottom' ? { flexDirection: 'column' as const } : {}),
    justifyContent: 'flex-end',
    // Explicitly opt back into pointer events. This panel portals to
    // `document.body` (see the `createPortal` call below), which puts it
    // outside the DOM subtree of any ancestor Radix `Dialog`/`AlertDialog`.
    // Radix's modal lockdown sets `document.body.style.pointerEvents =
    // 'none'` while open and restores `pointer-events: auto` only on its own
    // Content node -- any other element appended to `body`, including this
    // portal, inherits `none` and becomes inert to clicks. Setting `auto`
    // here (which cascades to the backdrop and panel below via normal CSS
    // inheritance) makes the drawer clickable regardless of which modal
    // lockdown mechanism, if any, is active on an ancestor. Same root cause
    // and fix as `StyledSelect` -- see
    // `packages/jumpstone-ui-react/src/components/styled_select/styled_select.tsx`.
    pointerEvents: 'auto',
  };

  const backdropStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(11,18,32,0.20)',
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
  };

  // `side="end"` (default) keeps its historic style object completely
  // untouched -- D2: zero change for existing consumers. `side="bottom"` is
  // a new, additive branch: full-width bottom sheet, top corners rounded,
  // height bounded by the live visual viewport (via `useVisualViewport`)
  // rather than a fixed `100%`, so the panel -- and its footer actions --
  // never end up hidden beneath an on-screen keyboard.
  const panelStyle: CSSProperties =
    side === 'bottom'
      ? {
          ...GLASS_STRONG,
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          maxHeight: get_bottom_sheet_max_height(visualViewport.height),
          // Shifts the sheet up so its bottom edge tracks the *visible*
          // visual viewport, not the full layout viewport -- on iOS Safari
          // an on-screen keyboard shrinks/pans visualViewport but does NOT
          // shrink the layout viewport, so without this the sheet's bottom
          // (and its footer actions) stays glued behind the keyboard. See
          // `get_bottom_sheet_keyboard_offset`'s doc comment for the
          // worked example. `marginBottom` (not `transform`) so it composes
          // with the overlay's flexbox `justifyContent: 'flex-end'`
          // without fighting the slide-in keyframe animation's own
          // `transform`.
          marginBottom: get_bottom_sheet_keyboard_offset({
            height: visualViewport.height,
            offsetTop: visualViewport.offsetTop,
            windowInnerHeight: visualViewport.windowInnerHeight,
          }),
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '16px 16px 0 0',
          animation: get_panel_animation(side),
        }
      : {
          ...GLASS_STRONG,
          position: 'relative',
          height: '100%',
          width: width === 'wide' ? 760 : 480,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          animation: get_panel_animation(side),
        };

  const headerStyle: CSSProperties = {
    padding: '1rem 1.5rem',
    borderBottom: '1px solid rgba(226,232,240,0.60)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  };

  const bodyStyle: CSSProperties = {
    flex: '1 1 0%',
    overflowY: 'auto',
    padding: '1rem 1.5rem',
  };

  const footerStyle: CSSProperties = {
    padding: '1rem 1.5rem',
    // Overrides the shorthand's bottom component -- `env()` resolves to 0 on
    // a device without a home-indicator safe area, so `max(1rem, 0)` is
    // 1rem, identical to the shorthand above. Only widens on devices that
    // report a non-zero `safe-area-inset-bottom` (notched phones).
    paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
    borderTop: '1px solid rgba(226,232,240,0.60)',
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'flex-end',
    flexShrink: 0,
  };

  const closeBtnStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2rem',
    height: '2rem',
    borderRadius: '6px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: 'var(--ink-muted)',
    transition: 'background 0.15s, color 0.15s',
    flexShrink: 0,
  };

  const titleStyle: CSSProperties = {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--ink)',
    letterSpacing: '-0.01em',
    margin: 0,
  };

  // Mirrors Dialog's `DialogDescription` className
  // (`mt-2 text-[13.5px] text-ink-muted leading-relaxed`) so a description
  // reads the same whether it renders in the tablet/desktop Dialog or the
  // phone Drawer bottom sheet.
  const descriptionStyle: CSSProperties = {
    marginTop: '0.5rem',
    fontSize: 13.5,
    color: 'var(--ink-muted)',
    lineHeight: 1.625,
  };

  const headerTextStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  };

  const panel = (
    <>
      {/* Inline keyframes — no CSS runtime needed */}
      <style>{`
        @keyframes drawer-slide-in {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
        @keyframes drawer-slide-in-bottom {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
      <div
        style={overlayStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        {/* Backdrop */}
        <div
          style={backdropStyle}
          onClick={onClose}
          aria-hidden="true"
          data-testid="drawer-backdrop"
        />
        {/* Panel — tabIndex={-1} allows programmatic focus when no children are focusable */}
        <div
          ref={panelRef}
          style={panelStyle}
          className={className}
          data-testid="drawer-panel"
          tabIndex={-1}
        >
          <div style={headerStyle}>
            {title !== undefined || description !== undefined ? (
              <div style={headerTextStyle}>
                {title !== undefined && (
                  <h2 id={titleId} style={titleStyle}>
                    {title}
                  </h2>
                )}
                {description !== undefined && (
                  <p id={descriptionId} style={descriptionStyle}>
                    {description}
                  </p>
                )}
              </div>
            ) : (
              <span />
            )}
            <button
              type="button"
              style={closeBtnStyle}
              onClick={onClose}
              aria-label={closeLabel ?? t('close_panel')}
              data-testid="drawer-close"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(15,23,42,0.06)';
                e.currentTarget.style.color = 'var(--ink)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--ink-muted)';
              }}
            >
              {/* X icon */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div style={bodyStyle} ref={bodyRef}>
            {children}
          </div>

          {footer !== undefined && <div style={footerStyle}>{footer}</div>}
        </div>
      </div>
    </>
  );

  return typeof document !== 'undefined'
    ? createPortal(panel, document.body)
    : null;
}
